import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Overlay } from '@alf/uikit';
import { PersonalOfficeWrapper } from '@namespace/account';
import { CommonLoader, ReferenceContext } from '@namespace/common';
import {
  FORM_COMPONENTS_TYPES,
  FORM_VALIDATION_TYPES,
  FormBuilder,
  useFormMethods
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { MAX_DATE_OFFSET, MIN_DATE_OFFSET, UserContext } from '@namespace/user';

import styles from './index.module.css';

const ProfileData = () => {
  const { t, f } = useI18n();
  const DATE_FORMATS_TYPES = f.getFormats();
  const [user, userActions] = useContext(UserContext);
  const { SET_CHANGE_USER, CHANGE_USER } = userActions;
  const { successNotification, errorNotification } = useNotifications();
  const [isFetching, setIsFetching] = useState(false);
  const {
    title,
    dateOfBirth,
    birthday,
    email,
    phoneNumber,
    login,
    question: selectSecretQuestion = '',
    firstName,
    lastName,
    adress,
    city,
    zip,
    id,
    accountStatuses
  } = user;
  const { isSubbedForEmails } = accountStatuses;
  const { secretQuestions } = useContext(ReferenceContext);

  const secretQuestionsOptions = useMemo(() => {
    const values = Object.values(secretQuestions);
    return values.length > 0
      ? values.map((question) => ({
          name: t(
            `register.secretQuestions.${question
              .toLowerCase()
              .replace(/\W/g, '')}`
          ),
          value: question
        }))
      : [];
  }, [secretQuestions, t]);

  const getSecretQuestion = () => {
    const providedQuestion = selectSecretQuestion;
    return (
      providedQuestion &&
      secretQuestionsOptions.find(
        ({ value: secretQuestion }) => secretQuestion === providedQuestion
      )
    );
  };

  const defaultValues = {
    userCountry: '',
    sex: title || '',
    birthday: f
      .getDateTime({ date: birthday || dateOfBirth })
      .toFormat(DATE_FORMATS_TYPES.CLIENT_API),
    email,
    phone: phoneNumber,
    firstName,
    lastName,
    middleName: '',
    secretQuestionAnswer: '',
    secretQuestion: getSecretQuestion(),
    username: login,
    adress,
    city,
    zip,
    id,
    subscription: isSubbedForEmails
  };

  const formMethods = useFormMethods({ defaultValues });
  const { watch, setValue } = formMethods;
  const phoneNumberValue = watch('phone');
  useEffect(() => {
    setValue('phone', `+${phoneNumberValue.replace(/\D/g, '')}`);
  }, [setValue, phoneNumberValue]);

  const minDateOffset = MIN_DATE_OFFSET;
  const maxDateOffset = MAX_DATE_OFFSET;

  const componentsList = useMemo(
    () => [
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'username',
        label: 'personalOffice.profile.data.username',
        disabled: true
      },

      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'id',
        label: 'personalOffice.profile.data.id',
        disabled: true
      },

      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'firstName',
        label: 'personalOffice.profile.data.mainInfo',
        disabled: true
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'lastName',
        disabled: true
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        label: 'personalOffice.profile.data.phone',
        name: 'phone',
        placeholder: phoneNumber
      },
      {
        type: FORM_COMPONENTS_TYPES.REG_DATE_PICKER,
        name: 'birthday',
        datePickerWrapperClassName: styles.fieldWrap,
        label: 'personalOffice.profile.data.birthday',
        disabled: true
      },
      {
        type: FORM_COMPONENTS_TYPES.EMAIL_INPUT_FIELD,
        name: 'email',
        label: 'personalOffice.profile.data.email',
        disabled: true
      },
      {
        type: FORM_COMPONENTS_TYPES.TOGGLE_CHECKBOX_INFO,
        name: 'subscription',
        label: 'personalOffice.profile.data.subscriptions'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'city',
        label: 'personalOffice.profile.data.address'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'adress'
      },
      {
        type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
        name: 'zip'
      },
      {
        type: FORM_COMPONENTS_TYPES.SUBMIT_BUTTON,
        value: 'personalOffice.profile.data.save',
        classNames: {
          className: styles.saveBtn
        },
        disabled: isFetching
      }
    ],
    [isFetching, phoneNumber]
  );

  const extendedComponentsList = useMemo(
    () => ({
      username: {
        classNames: {
          wrapperClassName: styles.fieldWrap
        }
      },
      id: {
        classNames: {
          wrapperClassName: styles.fieldWrap
        }
      },
      firstName: {
        classNames: {
          wrapperClassName: styles.fieldWrap
        }
      },
      lastName: {
        classNames: {
          wrapperClassName: styles.fieldWrapSmallTop
        }
      },
      phone: {
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          FORM_VALIDATION_TYPES.PHONE_NUMBER,
          {
            type: FORM_VALIDATION_TYPES.MIN_LENGTH_VALIDATOR,
            parameters: [12]
          },
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [13]
          }
        ],
        classNames: {
          wrapperClassName: styles.fieldWrap,
          inputClassName: styles.phone
        },
        isOnChangeValidation: true
      },
      birthday: {
        classNames: {
          datePickerWrapperClassName: styles.datePickerWrapper
        },
        format: DATE_FORMATS_TYPES.CLIENT_API
      },
      email: {
        classNames: {
          wrapperClassName: styles.fieldWrap
        }
      },
      subscription: {
        classNames: {
          wrapperClassName: styles.fieldWrap
        }
      },
      city: {
        classNames: {
          wrapperClassName: styles.fieldWrap
        },
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [40]
          }
        ]
      },
      adress: {
        classNames: {
          wrapperClassName: styles.fieldWrapSmallTop
        },
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [150]
          }
        ]
      },
      zip: {
        classNames: {
          wrapperClassName: styles.fieldWrapSmallTop
        },
        validationTypes: [
          FORM_VALIDATION_TYPES.REQUIRED,
          FORM_VALIDATION_TYPES.ZIP_CODE,
          FORM_VALIDATION_TYPES.NUMBER_FORMAT_VALIDATOR,
          {
            type: FORM_VALIDATION_TYPES.MAX_LENGTH_VALIDATOR,
            parameters: [5]
          },
          {
            type: FORM_VALIDATION_TYPES.MIN_LENGTH_VALIDATOR,
            parameters: [5]
          }
        ]
      }
    }),
    []
  );

  const modifiedComponentsList = useMemo(
    () =>
      componentsList.map((item) => ({
        ...item,
        ...extendedComponentsList[item.name]
      })),
    [componentsList, extendedComponentsList]
  );

  const onSubmit = useCallback(
    async (data) => {
      setIsFetching(true);

      try {
        const params = {
          phone: data.phone,
          city: data.city,
          adress: data.adress,
          zip: data.zip,
          subscription: data.subscription
        };
        const { phone } = await CHANGE_USER({ ...params, id });
        SET_CHANGE_USER({ ...params, phoneNumber: phone });
        successNotification(t('profile.data.profileSaved'));
      } catch ({ errorCode = 'accounting_error_6' }) {
        errorNotification(t(`accountingMessages.${errorCode}`));
      }

      setIsFetching(false);
    },
    [
      SET_CHANGE_USER,
      CHANGE_USER,
      errorNotification,
      id,
      successNotification,
      t
    ]
  );

  return (
    <Box
      component={PersonalOfficeWrapper}
      direction="column"
      className={styles.profileData}
    >
      {isFetching && (
        <Overlay>
          <CommonLoader />
        </Overlay>
      )}
      <Box className={styles.form}>
        <FormBuilder
          components={modifiedComponentsList}
          formMethods={formMethods}
          onSubmit={onSubmit}
          formProps={{ minDateOffset, maxDateOffset }}
        />
      </Box>
    </Box>
  );
};

export default ProfileData;
